import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { resolveTheme, type ThemeId } from "./registry";

interface ThemeContextValue {
  themeId: ThemeId;
}

const ThemeContext = createContext<ThemeContextValue>({ themeId: "default" });

/**
 * Reads the worker-injected hand-off (meta[name="theme"], same pattern as
 * meta[name="brand"]). The worker has already put data-theme on <html> and
 * injected the palette, so this provider sets nothing itself — it only
 * exposes which theme's slot components to render. Anything unknown or
 * missing resolves to "default".
 */
function readThemeFromDom(): ThemeId {
  if (typeof document === "undefined") return "default";
  const content =
    document
      .querySelector('meta[name="theme"]')
      ?.getAttribute("content") ?? undefined;
  return resolveTheme(content);
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const themeId = useMemo(readThemeFromDom, []);
  const value = useMemo(() => ({ themeId }), [themeId]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
